import {doc_getDocumentById_Api, doc_getExtraDataById_Api} from 'apis'
import {documentSchema} from 'apis/schema'
import EmptyHolder from 'components/EmptyHolder'
import {FullSideModalLayout} from 'components/Modal/SideModalLayout'
import getTitle from 'helpers/getTitle'
import _ from 'lodash'
import Async from 'modules/asyncCache/components/Async'
import useAsync from 'modules/asyncCache/useAsync'
import useTranslate from 'modules/local/useTranslate'
import PermissionProvider from 'modules/permissions/PermissionProvider'
import React, {useCallback, useMemo, useRef, useState} from 'react'
import {useSelectEntities} from 'redux/entities/useSelectEntities'
import {useCryptoJS} from 'views/Auth/LoginForm'
import {bindQueryParam} from 'views/AppLocals/functions/routerHelper'
import {getResponseItem, NullAsDefault} from 'views/Shared'

const DocumentContext = React.createContext({})

export const DocumentActionTypes = Object.freeze({
  SHOW_INFO_MODAL: 'show-info-modal',
  SHOW_ADMIN_MODAL: 'show-admin-modal',
  SHOW_LOGS_MODAL: 'show-logs-modal',
  SHOW_INDEXES_MODAL: 'show-indexes-modal',
  SHOW_RELEVANT_DOCS_MODAL: 'show-relevant-docs-modal',
  SHOW_DEFINITION_MODAL: 'show-definition-modal',
  SHOW_RELATED_CONTENT_MODAL: 'show-related-docs-modal',
})

export const DocumentProvider = ({ id, children, ...props }) => {
  const t = useTranslate()

  const {
    success,
    response,
    isLoading = false,
    ...rest
  } = useAsync({
    query: bindQueryParam({ id }),
    apiInfo: doc_getDocumentById_Api,
    cacheId: doc_getDocumentById_Api.path.replace(':id', id),
  })

  const { encrypt } = useCryptoJS('__document_secret_key')

  const scrollRef = useRef(null)

  const [rows, setRows] = useState([])

  const [hasMore, setHasMore] = useState(true)

  const [page_loading, set_page_loading] = useState(false)

  const [modal_action, set_modal_action] = useState({
    type: undefined,
    value: undefined,
  })

  const error = getResponseItem(response, 'error')

  const errorMessage = _.get(response, 'errorMessages.error')

  const error_code = _.get(response, 'errorMessages.error_code')

  const status = {
    title: getResponseItem(response, 'status'),
    description: errorMessage,
  }

  const isSucceed = Number(_.get(response, 'status')) === 200

  const item = useSelectEntities(id, documentSchema, getResponseItem(response))

  const onLoadMore = () => {
    set_page_loading(true)
    setTimeout(() => {
      if (scrollRef?.current) {
        scrollRef.current.scrollIntoView(true)
      }
    }, 100)
  }

  const onDismiss = () => {
    set_modal_action({})
  }

  const handleAction = useCallback((action, data = {}) => {
    switch (action) {
      case DocumentActionTypes.SHOW_INFO_MODAL:
      case DocumentActionTypes.SHOW_LOGS_MODAL:
      case DocumentActionTypes.SHOW_ADMIN_MODAL:
      case DocumentActionTypes.SHOW_INDEXES_MODAL:
      case DocumentActionTypes.SHOW_RELEVANT_DOCS_MODAL:
      case DocumentActionTypes.SHOW_DEFINITION_MODAL:
      case DocumentActionTypes.SHOW_RELATED_CONTENT_MODAL:
        set_modal_action({
          type: action,
          value: data,
        })
        break
      default:
        break
    }
  }, [])

  const modalDismissRef = useRef(NullAsDefault)

  const renderModal = ({
    title,
    onClose = NullAsDefault,
    withProps = () => ({}),
    Component = EmptyHolder,
  }) => {
    return (
      <FullSideModalLayout
        center={true}
        onCancel={() => {
          onClose()
          onDismiss()
        }}
        headerStyle={{ padding: '0.5rem' }}
        title={_.isString(title) ? t(title) : title}>
        <Component {...(withProps() ?? {})} />
      </FullSideModalLayout>
    )
  }

  const modal_content = useMemo(() => {
    const codi_header = (
      <span className="max-lines-2 font-medium text-sm text-color-000">
        {getTitle(item)}
      </span>
    )
    const params = modal_action?.value ?? {}
    switch (modal_action?.type) {
      case DocumentActionTypes.SHOW_INDEXES_MODAL:
        return renderModal({
          title: codi_header,
          Component: params.Component,
          onClose: modalDismissRef?.current,
          withProps: () => ({ onItemClick: onDismiss }),
        })
      case DocumentActionTypes.SHOW_ADMIN_MODAL:
        return renderModal({
          title: 'settings',
          Component: params.Component,
          onClose: modalDismissRef?.current,
        })
      case DocumentActionTypes.SHOW_INFO_MODAL:
      case DocumentActionTypes.SHOW_LOGS_MODAL:
      case DocumentActionTypes.SHOW_RELEVANT_DOCS_MODAL:
      case DocumentActionTypes.SHOW_DEFINITION_MODAL:
      case DocumentActionTypes.SHOW_RELATED_CONTENT_MODAL:
        return renderModal({
          title: codi_header,
          Component: params.Component,
          onClose: modalDismissRef?.current,
        })
      default:
        break
    }
    return null
  }, [item, modal_action, modalDismissRef])

  return (
    <DocumentContext.Provider
      key={id}
      value={{
        item,
        error,
        status,
        success,
        isLoading,
        isSucceed,
        error_code,
        errorMessage,
        rows,
        hasMore,
        setRows,
        scrollRef,
        setHasMore,
        onLoadMore,
        page_loading,
        handleAction,
        modal_content,
        modalDismissRef,
        set_page_loading,
        createRowHash: encrypt,
        ...props,
        ...rest,
      }}>
      <PermissionProvider
        permissions={{
          edit: !!_.get(item, 'edit'),
          ..._.get(item, 'available_actions', {}),
        }}>
        <React.Fragment>
          {children}
          {modal_content}
          {!!success && (
            <Async
              query={bindQueryParam({ id })}
              apiInfo={doc_getExtraDataById_Api}
              // cacheId={doc_getExtraDataById_Api.path.replace(':id', id)}
            />
          )}
        </React.Fragment>
      </PermissionProvider>
    </DocumentContext.Provider>
  )
}

export default DocumentContext
